import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtINR } from "../lib/api";
import { useShop } from "../lib/shop";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Plus, ShieldCheck, MapPin, Check, X } from "lucide-react";
import { INDIAN_STATES } from "../lib/india";
import { useSEO } from "../lib/useSEO";

export default function Checkout() {
  useSEO({ title: "Checkout | Joy Pearl", description: "Complete your Joy Pearl order with secure OTP-verified Cash on Delivery across India.", noIndex: true });
  const { cart, refreshCart } = useShop();
  const { user, loading, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState("");
  const [orderInfo, setOrderInfo] = useState(null);
  const [otp, setOtp] = useState("");
  const [adding, setAdding] = useState(false);
  const [newAddr, setNewAddr] = useState({ name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", landmark: "", is_default: true });
  const [pinCheck, setPinCheck] = useState(null);
  const [pinChecking, setPinChecking] = useState(false);
  const [selectedPinCheck, setSelectedPinCheck] = useState(null);
  const [settings, setSettings] = useState({ cod_handling_fee: 29, cod_max_amount: 5000, free_shipping_over: 499 });
  const [submitting, setSubmitting] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null); // {code, discount}
  const [couponChecking, setCouponChecking] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/auth/addresses").then((r) => {
      setAddresses(r.data);
      const def = r.data.find((a) => a.is_default) || r.data[0];
      if (def) setSelectedAddrId(def.id);
    });
    api.get("/settings/public").then((r) => setSettings(r.data));
  }, [user, loading, navigate]);

  useEffect(() => {
    const pin = newAddr.pincode;
    setPinCheck(null);
    if (pin.length !== 6 || !/^\d{6}$/.test(pin)) return;
    setPinChecking(true);
    api.get(`/pincodes/check/${pin}`)
      .then((r) => {
        setPinCheck(r.data);
        if (r.data.serviceable && r.data.city) {
          setNewAddr((a) => ({ ...a, city: a.city || r.data.city, state: a.state || r.data.state }));
        }
      })
      .catch(() => setPinCheck({ serviceable: false, message: "Could not verify pincode" }))
      .finally(() => setPinChecking(false));
  }, [newAddr.pincode]);

  useEffect(() => {
    if (!selectedAddrId) { setSelectedPinCheck(null); return; }
    const a = addresses.find((x) => x.id === selectedAddrId);
    if (!a) return;
    api.get(`/pincodes/check/${a.pincode}`).then((r) => setSelectedPinCheck(r.data)).catch(() => setSelectedPinCheck(null));
  }, [selectedAddrId, addresses]);

  if (loading) return <div className="jp-container py-20 text-center text-brand-muted">Loading…</div>;
  if (!user) return null;
  if (!cart.items?.length && !orderInfo) return (
    <div className="jp-container py-20 text-center">
      <p className="text-brand-muted">Your bag is empty.</p>
      <Link to="/products" className="jp-btn-primary mt-4 inline-flex">Browse</Link>
    </div>
  );

  const submitAddress = async (e) => {
    e.preventDefault();
    if (!pinCheck?.serviceable) { toast.error("Please enter a serviceable Indian pincode"); return; }
    try {
      const { data } = await api.post("/auth/addresses", newAddr);
      const refreshed = await api.get("/auth/addresses");
      setAddresses(refreshed.data);
      setSelectedAddrId(data.id);
      await refreshMe();
      setAdding(false);
      setNewAddr({ name: "", phone: "", line1: "", line2: "", city: "", state: "", pincode: "", landmark: "", is_default: false });
      toast.success("Address added");
    } catch { toast.error("Could not save address"); }
  };

  const verifyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponChecking(true);
    try {
      const { data } = await api.post("/coupons/validate", { code: couponCode.trim(), cart_subtotal: cart.subtotal });
      setCouponApplied(data);
      toast.success(`Coupon applied: ₹${data.discount} off`);
    } catch (e) {
      setCouponApplied(null);
      toast.error(e?.response?.data?.detail || "Invalid coupon");
    } finally { setCouponChecking(false); }
  };
  const removeCoupon = () => { setCouponApplied(null); setCouponCode(""); };

  const placeOrder = async () => {
    if (!selectedAddrId) { toast.error("Please select an address"); return; }
    if (selectedPinCheck && !selectedPinCheck.serviceable) { toast.error("Selected pincode is not serviceable yet."); return; }
    if (cart.subtotal > settings.cod_max_amount) {
      toast.error(`COD limit is ₹${settings.cod_max_amount}. Please reduce items.`); return;
    }
    setSubmitting(true);
    try {
      const payload = { address_id: selectedAddrId };
      if (couponApplied) payload.coupon_code = couponApplied.code;
      const { data } = await api.post("/checkout", payload);
      setOrderInfo(data);
      toast.success("OTP generated. Verify to confirm your COD order.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); }
    finally { setSubmitting(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/orders/verify-otp", { order_id: orderInfo.order_id, otp });
      toast.success("Order placed!");
      await refreshCart();
      navigate(`/orders/${orderInfo.order_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid OTP"); }
    finally { setSubmitting(false); }
  };

  if (orderInfo) {
    return (
      <div className="jp-container py-12 max-w-md" data-testid="checkout-otp">
        <h1 className="font-serif text-3xl mb-2">Verify your order</h1>
        <p className="text-brand-muted text-sm mb-6">
          {orderInfo.sms_status === "sent" && orderInfo.sms_provider === "msg91"
            ? <>We've sent a 6-digit OTP via SMS to confirm your Cash on Delivery order <strong>{orderInfo.order_number}</strong>.</>
            : <>We've generated a 6-digit OTP to confirm your Cash on Delivery order <strong>{orderInfo.order_number}</strong>.</>}
        </p>
        {orderInfo.otp_dev_preview && (
          <div className="bg-brand-blush border border-brand-line p-4 mb-6 text-sm">
            <span className="jp-eyebrow">Dev OTP (SMS provider not configured)</span>
            <p className="font-mono text-2xl tracking-[0.4em] text-brand-primary mt-2" data-testid="otp-dev-preview">{orderInfo.otp_dev_preview}</p>
            <p className="text-xs text-brand-muted mt-2">To enable real SMS, set MSG91_AUTH_KEY / MSG91_TEMPLATE_ID / MSG91_SENDER_ID in the backend env.</p>
          </div>
        )}
        <form onSubmit={verifyOtp}>
          <label className="jp-eyebrow">Enter OTP</label>
          <input value={otp} onChange={(e) => setOtp(e.target.value)} className="jp-input mt-2 tracking-[0.4em] text-center text-xl font-mono" maxLength={6} required data-testid="otp-input"/>
          <button type="submit" disabled={submitting} className="jp-btn-primary w-full mt-6" data-testid="otp-verify">{submitting ? "Verifying…" : "Verify & Place Order"}</button>
          <button type="button" onClick={async () => {
            try {
              const { data } = await api.post(`/orders/${orderInfo.order_id}/resend-otp`);
              setOrderInfo({ ...orderInfo, ...data });
              toast.success(data.sms_status === "sent" && data.sms_provider === "msg91" ? "OTP resent via SMS" : "New OTP generated");
            } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend"); }
          }} className="jp-btn-outline w-full mt-3" data-testid="otp-resend">Resend OTP</button>
        </form>
      </div>
    );
  }

  const codFee = settings.cod_handling_fee || 0;
  const discount = couponApplied?.discount || 0;
  const totalWithCod = cart.total + codFee - discount;
  const overCodLimit = cart.subtotal > settings.cod_max_amount;

  return (
    <div className="jp-container py-8 md:py-12" data-testid="checkout-page">
      <h1 className="font-serif text-3xl md:text-5xl mb-8">Checkout</h1>
      <div className="grid md:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-8">
          <section className="bg-white border border-brand-line p-6">
            <h2 className="font-serif text-xl mb-4">Delivery Address (India only)</h2>
            {addresses.length === 0 && <p className="text-sm text-brand-muted mb-4">No addresses saved. Add one to continue.</p>}
            <div className="space-y-3">
              {addresses.map((a) => (
                <label key={a.id} className={`block border p-4 cursor-pointer ${selectedAddrId === a.id ? "border-brand-primary bg-brand-blush/40" : "border-brand-line"}`} data-testid={`address-${a.id}`}>
                  <input type="radio" name="addr" checked={selectedAddrId === a.id} onChange={() => setSelectedAddrId(a.id)} className="mr-2"/>
                  <span className="font-medium">{a.name}</span> <span className="text-sm text-brand-muted">• +91 {a.phone}</span>
                  <p className="text-sm text-brand-muted mt-1">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.pincode}</p>
                </label>
              ))}
            </div>
            {selectedAddrId && selectedPinCheck && !selectedPinCheck.serviceable && (
              <p className="text-xs text-red-700 mt-3 flex items-center gap-1" data-testid="selected-pin-unserviceable">
                <X size={14}/> Sorry, we don't deliver to {selectedPinCheck.pincode} yet.
              </p>
            )}
            {!adding ? (
              <button onClick={() => setAdding(true)} className="jp-btn-outline mt-4" data-testid="add-address-btn"><Plus size={14} className="mr-2"/>Add new address</button>
            ) : (
              <form onSubmit={submitAddress} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="new-address-form">
                <input required placeholder="Full name" className="jp-input" value={newAddr.name} onChange={(e) => setNewAddr({...newAddr, name: e.target.value})}/>
                <div className="flex items-center border-b border-brand-line">
                  <span className="text-sm text-brand-muted mr-2">+91</span>
                  <input required placeholder="10-digit phone" pattern="\d{10}" className="jp-input border-none focus:ring-0" value={newAddr.phone} onChange={(e) => setNewAddr({...newAddr, phone: e.target.value.replace(/\D/g, "").slice(0, 10)})} data-testid="addr-phone"/>
                </div>
                <input required placeholder="House / Flat / Address line 1" className="jp-input sm:col-span-2" value={newAddr.line1} onChange={(e) => setNewAddr({...newAddr, line1: e.target.value})}/>
                <input placeholder="Area / Street (optional)" className="jp-input sm:col-span-2" value={newAddr.line2} onChange={(e) => setNewAddr({...newAddr, line2: e.target.value})}/>

                <div className="sm:col-span-2 relative">
                  <input required placeholder="Pincode (6-digit)" pattern="\d{6}" maxLength={6} className="jp-input" value={newAddr.pincode}
                    onChange={(e) => setNewAddr({...newAddr, pincode: e.target.value.replace(/\D/g, "").slice(0, 6)})} data-testid="addr-pincode"/>
                  {pinChecking && <span className="absolute right-0 top-2 text-xs text-brand-muted">Checking…</span>}
                  {pinCheck && (
                    pinCheck.serviceable ? (
                      <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1" data-testid="pin-ok"><Check size={14}/> Delivers to {pinCheck.city}, {pinCheck.state} • ~{pinCheck.eta_days} days</p>
                    ) : (
                      <p className="text-xs text-red-700 mt-1 flex items-center gap-1" data-testid="pin-fail"><X size={14}/> {pinCheck.message}</p>
                    )
                  )}
                </div>

                <input required placeholder="City" className="jp-input" value={newAddr.city} onChange={(e) => setNewAddr({...newAddr, city: e.target.value})}/>
                <select required className="jp-input" value={newAddr.state} onChange={(e) => setNewAddr({...newAddr, state: e.target.value})} data-testid="addr-state">
                  <option value="">Select State / UT…</option>
                  {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input placeholder="Landmark (optional)" className="jp-input sm:col-span-2" value={newAddr.landmark} onChange={(e) => setNewAddr({...newAddr, landmark: e.target.value})}/>
                <div className="sm:col-span-2 flex gap-3">
                  <button type="submit" disabled={!pinCheck?.serviceable} className="jp-btn-primary" data-testid="save-address-btn">Save Address</button>
                  <button type="button" onClick={() => setAdding(false)} className="jp-btn-outline">Cancel</button>
                </div>
              </form>
            )}
          </section>

          <section className="bg-white border border-brand-line p-6">
            <h2 className="font-serif text-xl mb-2">Payment Method</h2>
            <label className="flex items-start gap-3 p-4 border border-brand-primary bg-brand-blush/40 mt-3">
              <input type="radio" checked readOnly className="mt-1" data-testid="payment-cod"/>
              <div className="flex-1">
                <p className="font-medium">Cash on Delivery <span className="jp-badge ml-2">Recommended</span></p>
                <p className="text-sm text-brand-muted mt-1">Pay in cash when your order arrives. A handling fee of {fmtINR(codFee)} applies. OTP confirmation required at checkout.</p>
                <p className="text-xs text-brand-muted mt-2">COD limit: orders up to {fmtINR(settings.cod_max_amount)}. Higher-value orders will be supported with online payment in a future update.</p>
              </div>
            </label>
            {overCodLimit && (
              <p className="text-sm text-red-700 mt-3" data-testid="cod-over-limit">
                Your bag total {fmtINR(cart.subtotal)} exceeds the COD limit of {fmtINR(settings.cod_max_amount)}.
              </p>
            )}
          </section>
        </div>

        <aside className="bg-white border border-brand-line p-6 h-fit md:sticky md:top-24" data-testid="checkout-summary">
          <h2 className="font-serif text-xl mb-5">Summary</h2>
          <div className="space-y-3 text-sm max-h-48 overflow-auto">
            {cart.items.map((it) => (
              <div key={`${it.product_id}-${it.variant_sku || ""}`} className="flex gap-3">
                <div className="h-14 w-14 bg-brand-blush shrink-0 overflow-hidden">{it.image && <img src={it.image} alt={it.name} className="h-full w-full object-cover"/>}</div>
                <div className="flex-1">
                  <p className="leading-snug line-clamp-2">{it.name}</p>
                  {it.variant_label && <p className="text-[10px] text-brand-muted mt-0.5">{it.variant_label}</p>}
                  <p className="text-xs text-brand-muted mt-0.5">Qty {it.qty}</p>
                </div>
                <span className="text-sm">{fmtINR(it.price * it.qty)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-brand-line my-5"/>
          <div className="text-sm space-y-2">
            <div className="flex justify-between"><span className="text-brand-muted">Subtotal</span><span>{fmtINR(cart.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-brand-muted">Shipping</span><span>{cart.shipping === 0 ? "Free" : fmtINR(cart.shipping)}</span></div>
            <div className="flex justify-between" data-testid="cod-fee"><span className="text-brand-muted">COD handling fee</span><span>{fmtINR(codFee)}</span></div>
            {couponApplied && (
              <div className="flex justify-between text-emerald-700" data-testid="coupon-discount">
                <span>Coupon {couponApplied.code}</span><span>− {fmtINR(discount)}</span>
              </div>
            )}
          </div>

          {/* Coupon input */}
          <div className="mt-4 border-t border-brand-line pt-4" data-testid="coupon-section">
            {!couponApplied ? (
              <div className="flex gap-2">
                <input
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Coupon code (try WELCOME10)"
                  className="jp-input flex-1 uppercase"
                  data-testid="coupon-input"
                />
                <button onClick={verifyCoupon} disabled={couponChecking || !couponCode.trim()} className="jp-btn-outline px-3 py-2 text-xs" data-testid="coupon-apply">
                  {couponChecking ? "…" : "Apply"}
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between border border-emerald-700/30 bg-emerald-50 p-2 text-sm">
                <span className="text-emerald-700">✓ {couponApplied.code} applied</span>
                <button onClick={removeCoupon} className="text-xs text-brand-muted hover:text-red-700" data-testid="coupon-remove">Remove</button>
              </div>
            )}
          </div>
          <div className="border-t border-brand-line my-5"/>
          <div className="flex justify-between text-lg font-semibold mb-6">
            <span>Total</span><span data-testid="checkout-total">{fmtINR(totalWithCod)}</span>
          </div>
          <button
            onClick={placeOrder}
            disabled={!selectedAddrId || submitting || overCodLimit || (selectedPinCheck && !selectedPinCheck.serviceable)}
            className="jp-btn-primary w-full" data-testid="place-order-btn">
            {submitting ? "Processing…" : "Place Order (COD)"}
          </button>
          <p className="text-xs text-brand-muted mt-3 inline-flex items-center gap-1"><ShieldCheck size={12}/> Secured by OTP verification</p>
          <p className="text-xs text-brand-muted mt-1 inline-flex items-center gap-1"><MapPin size={12}/> Delivers within India only</p>
        </aside>
      </div>
    </div>
  );
}
